/**
 * workflowStatus — Source de verite unique pour les statuts metier Cinqo
 *
 * Remplace les 14 dictionnaires dupliques `{ label, color, bg }` disperses
 * dans apps/ops (JOB_STATUS_LABELS, STATUS_COLORS, STAGES, BILLING_STATUS,
 * STATUS_PILL, STATUS_CONFIG, STATUS_LABELS, etc.).
 *
 * Chaque status est mappe a un intent du DS (success/warning/error/info/neutral)
 * + un labelFr canonique. Les couleurs hex sont conservees pour les consumers
 * qui en ont besoin (CalendarView, generation PDF, legacy inline styles).
 *
 * Usage moderne (prefere) :
 *   import { StatusBadge } from '@cinqo/ui'
 *   import { EVACUATION_STATUS } from '@cinqo/ui/lib/workflowStatus'
 *   const cfg = EVACUATION_STATUS[evac.status] ?? EVACUATION_STATUS.__unknown
 *   <StatusBadge intent={cfg.intent}>{cfg.labelFr}</StatusBadge>
 *
 * Usage legacy (PDF, inline style, calendar event) :
 *   <span style={{ color: cfg.colorHex, backgroundColor: cfg.bgHex }}>
 *
 * Domain-namespaced : meme cle (`draft`, `validated`, `completed`) peut avoir
 * un sens different selon le contexte (offre vs audit vs facture) — on evite
 * les collisions en exposant un map par domaine.
 */

// ─────────────────────────────────────────────────────────────────────
// Types
// ─────────────────────────────────────────────────────────────────────

/** Intent compatible avec <StatusBadge intent={...} /> du DS. */
export type StatusIntent = 'success' | 'warning' | 'error' | 'info' | 'neutral'

/** Configuration canonique d'un status metier. */
export interface StatusConfig {
  /** Intent DS (pour StatusBadge, Badge, alerts). */
  intent: StatusIntent
  /** Label francais canonique (sans accent pour compat historique). */
  labelFr: string
  /**
   * Couleur texte hex — pour consumers legacy (CalendarView, generation PDF).
   * Aligne sur la palette Tailwind tokens : primary, success, warning, error, info.
   * A terme ces consumers migreront vers les tokens CSS et on pourra supprimer.
   */
  colorHex: string
  /** Couleur background hex — meme logique que colorHex. */
  bgHex: string
}

/** Helper pour fallback safe quand le status est inconnu. */
export const UNKNOWN_STATUS: StatusConfig = {
  intent: 'neutral',
  labelFr: 'Inconnu',
  colorHex: '#7A7A8A',
  bgHex: '#F7F7F9',
}

/**
 * Retourne la config d'un status avec fallback neutre.
 *
 * @param map Un des dictionnaires exportes par ce module.
 * @param key Cle status a chercher.
 * @param overrideLabel Si defini ET fallback, remplace le label par defaut "Inconnu".
 */
export function getStatusConfig(
  map: Record<string, StatusConfig>,
  key: string | null | undefined,
  overrideLabel?: string,
): StatusConfig {
  if (!key) return overrideLabel ? { ...UNKNOWN_STATUS, labelFr: overrideLabel } : UNKNOWN_STATUS
  const cfg = map[key]
  if (cfg) return cfg
  return overrideLabel
    ? { ...UNKNOWN_STATUS, labelFr: overrideLabel }
    : { ...UNKNOWN_STATUS, labelFr: key }
}

// ─────────────────────────────────────────────────────────────────────
// Palette hex interne — alignee Tailwind tokens
// ─────────────────────────────────────────────────────────────────────
// Cache : ces constantes evitent les magic-strings dans les maps ci-dessous
// et permettent de tracer l'intent -> couleur en un coup d'oeil.
//
// brand (primary violet)  : #3D2ECC / bg #EEF0FF
// info   (bleu)           : #2563EB / bg #EFF6FF
// success (vert)          : #059669 / bg #ECFDF5
// warning (orange/amber)  : #D97706 / bg #FFFBEB
// error  (rouge)          : #DC2626 / bg #FEF2F2
// neutral (gris)          : #7A7A8A / bg #F7F7F9
// muted  (gris clair)     : #9CA3AF / bg #F3F4F6

const C = {
  brand:   { color: '#3D2ECC', bg: '#EEF0FF' },
  info:    { color: '#2563EB', bg: '#EFF6FF' },
  success: { color: '#059669', bg: '#ECFDF5' },
  warning: { color: '#D97706', bg: '#FFFBEB' },
  warningSoft: { color: '#F5A623', bg: '#FFF8EB' },
  error:   { color: '#DC2626', bg: '#FEF2F2' },
  errorSoft: { color: '#E24B3C', bg: '#FDECEA' },
  neutral: { color: '#7A7A8A', bg: '#F7F7F9' },
  muted:   { color: '#9CA3AF', bg: '#F3F4F6' },
  purple:  { color: '#7C3AED', bg: '#F5F3FF' },
  successSoft: { color: '#2EC9A0', bg: '#E6FAF5' },
} as const

// ─────────────────────────────────────────────────────────────────────
// Evacuation / Flux Demande — cycle de vie cote client & regulateur
// ─────────────────────────────────────────────────────────────────────

/**
 * Remplace :
 *   - lib/state-machine.ts `statusConfig`
 *   - pages/ClientPortal.shared.ts `STATUS_LABELS`
 *   - components/CalendarView.tsx `STATUS_COLORS` (subset evacuation)
 */
export const EVACUATION_STATUS: Record<string, StatusConfig> = {
  requested:        { intent: 'neutral', labelFr: 'Demande envoyee',          colorHex: C.neutral.color, bgHex: C.neutral.bg },
  counter_proposed: { intent: 'warning', labelFr: 'Date alternative proposee', colorHex: C.warningSoft.color, bgHex: C.warningSoft.bg },
  confirmed:        { intent: 'info',    labelFr: 'Confirmee',                 colorHex: C.brand.color,  bgHex: C.brand.bg },
  in_progress:      { intent: 'warning', labelFr: 'En cours',                  colorHex: C.warningSoft.color, bgHex: C.warningSoft.bg },
  needs_resolution: { intent: 'error',   labelFr: 'A resoudre',                colorHex: C.errorSoft.color, bgHex: C.errorSoft.bg },
  completed:        { intent: 'success', labelFr: 'Terminee',                  colorHex: C.successSoft.color, bgHex: C.successSoft.bg },
  cancelled:        { intent: 'neutral', labelFr: 'Annulee',                   colorHex: C.muted.color,  bgHex: C.muted.bg },
}

// ─────────────────────────────────────────────────────────────────────
// Job — cycle de vie cote partenaire/recycleur
// ─────────────────────────────────────────────────────────────────────

/**
 * Remplace :
 *   - pages/PartnerPortal.shared.ts `JOB_STATUS_LABELS`
 *   - components/CalendarView.tsx `STATUS_COLORS` (subset partenaire)
 */
export const JOB_STATUS: Record<string, StatusConfig> = {
  assigned:     { intent: 'info',    labelFr: 'Nouveau',        colorHex: C.brand.color,      bgHex: C.brand.bg },
  accepted:     { intent: 'success', labelFr: 'Accepte',        colorHex: C.successSoft.color, bgHex: C.successSoft.bg },
  in_transit:   { intent: 'warning', labelFr: 'En route',       colorHex: C.warningSoft.color, bgHex: C.warningSoft.bg },
  service_done: { intent: 'success', labelFr: 'Rapport soumis', colorHex: C.success.color,    bgHex: C.success.bg },
  validated:    { intent: 'neutral', labelFr: 'Valide',         colorHex: '#6B7280',          bgHex: C.muted.bg },
  failed:       { intent: 'error',   labelFr: 'Echoue',         colorHex: C.errorSoft.color,  bgHex: C.errorSoft.bg },
  cancelled:    { intent: 'neutral', labelFr: 'Annule',         colorHex: C.muted.color,      bgHex: C.muted.bg },
}

// ─────────────────────────────────────────────────────────────────────
// Operations V2 stages — console regulateur
// ─────────────────────────────────────────────────────────────────────

/**
 * Remplace : pages/RegulationPage.tsx `STAGES`
 */
export const OPERATION_STAGE: Record<string, StatusConfig> = {
  awaiting_dispatch:           { intent: 'neutral', labelFr: 'Dispatch',          colorHex: C.neutral.color, bgHex: C.neutral.bg },
  dispatch_failed:             { intent: 'error',   labelFr: 'Dispatch echoue',   colorHex: C.errorSoft.color, bgHex: C.error.bg },
  awaiting_partner_acceptance: { intent: 'warning', labelFr: 'Attente recycleur', colorHex: C.warning.color, bgHex: C.warning.bg },
  scheduled:                   { intent: 'info',    labelFr: 'Planifiee',         colorHex: C.info.color,    bgHex: C.info.bg },
  in_field:                    { intent: 'info',    labelFr: 'Collecte',          colorHex: C.purple.color,  bgHex: C.purple.bg },
  awaiting_regulator_review:   { intent: 'warning', labelFr: 'A valider',         colorHex: C.warning.color, bgHex: C.warning.bg },
  validated:                   { intent: 'success', labelFr: 'Validee',           colorHex: C.success.color, bgHex: C.success.bg },
  completed:                   { intent: 'success', labelFr: 'Terminee',          colorHex: C.success.color, bgHex: C.success.bg },
  cancelled:                   { intent: 'neutral', labelFr: 'Annulee',           colorHex: C.neutral.color, bgHex: C.neutral.bg },
  inconsistent:                { intent: 'error',   labelFr: 'Incoherent',        colorHex: C.error.color,   bgHex: C.error.bg },
}

// ─────────────────────────────────────────────────────────────────────
// Offre / Flux contractuel (draft/sent/accepted/active/archived/declined)
// ─────────────────────────────────────────────────────────────────────

/**
 * Remplace :
 *   - lib/offres-helpers.ts `STATUS_LABELS`
 *   - pages/Containers.tsx `STATUS_CONFIG`
 */
export const OFFER_STATUS: Record<string, StatusConfig> = {
  draft:    { intent: 'neutral', labelFr: 'Brouillon',  colorHex: '#6B7280',           bgHex: C.muted.bg },
  sent:     { intent: 'warning', labelFr: 'Envoyée',    colorHex: C.warningSoft.color, bgHex: C.warningSoft.bg },
  accepted: { intent: 'info',    labelFr: 'Acceptée',   colorHex: C.brand.color,       bgHex: C.brand.bg },
  active:   { intent: 'success', labelFr: 'Active',     colorHex: C.successSoft.color, bgHex: C.successSoft.bg },
  declined: { intent: 'error',   labelFr: 'Refusée',    colorHex: C.errorSoft.color,   bgHex: C.errorSoft.bg },
  archived: { intent: 'neutral', labelFr: 'Archivée',   colorHex: C.muted.color,       bgHex: C.muted.bg },
}

// ─────────────────────────────────────────────────────────────────────
// Audit synoptique
// ─────────────────────────────────────────────────────────────────────

/**
 * Remplace :
 *   - pages/AuditsPage.tsx `STATUS_LABELS` + `STATUS_STYLES`
 *   - pages/AuditDetailPage.tsx idem
 *   - pages/ClientAuditPage.tsx idem
 */
export const AUDIT_STATUS: Record<string, StatusConfig> = {
  draft:    { intent: 'neutral', labelFr: 'Brouillon', colorHex: C.neutral.color, bgHex: C.neutral.bg },
  emitted:  { intent: 'info',    labelFr: 'Emis',      colorHex: C.info.color,    bgHex: C.info.bg },
  archived: { intent: 'neutral', labelFr: 'Archive',   colorHex: C.muted.color,   bgHex: C.muted.bg },
}

// ─────────────────────────────────────────────────────────────────────
// Facture client / facture recycleur / facture consolidee
// ─────────────────────────────────────────────────────────────────────

/**
 * Facture client Cinqo (Cinqo -> client).
 * Remplace : pages/ClientInvoicesPage.tsx `STATUS_CONFIG`
 */
export const CLIENT_INVOICE_STATUS: Record<string, StatusConfig> = {
  draft:   { intent: 'neutral', labelFr: 'Brouillon', colorHex: C.neutral.color, bgHex: C.neutral.bg },
  emitted: { intent: 'info',    labelFr: 'Emise',     colorHex: C.info.color,    bgHex: C.info.bg },
  paid:    { intent: 'success', labelFr: 'Payee',     colorHex: C.success.color, bgHex: C.success.bg },
}

/**
 * Facture recycleur (recycleur -> Cinqo).
 * Remplace :
 *   - pages/PartnerFacturationPage.tsx `STATUS_PILL`
 *   - components/PartnerInvoicesTab.tsx `STATUS_CONFIG`
 */
export const PARTNER_INVOICE_STATUS: Record<string, StatusConfig> = {
  draft:     { intent: 'neutral', labelFr: 'Brouillon', colorHex: C.neutral.color, bgHex: C.neutral.bg },
  sent:      { intent: 'info',    labelFr: 'Envoyee',   colorHex: C.info.color,    bgHex: C.info.bg },
  validated: { intent: 'success', labelFr: 'Validee',   colorHex: C.success.color, bgHex: C.success.bg },
  paid:      { intent: 'success', labelFr: 'Payee',     colorHex: C.success.color, bgHex: C.success.bg },
}

/**
 * Facture consolidee (multi-flux, multi-periode).
 * Remplace : components/ConsolidatedInvoicesTab.tsx `STATUS_PILL`
 */
// Label "Émise" unifié pour `emitted` (invoices.status) ET `sent`
// (partner_invoices.status / commission_status). Sémantiquement identiques
// côté métier (facture émise + envoyée par email au destinataire). Les 2
// enums DB sont historiques, on n'aligne pas la DB pour rester chirurgical.
export const CONSOLIDATED_INVOICE_STATUS: Record<string, StatusConfig> = {
  draft:   { intent: 'neutral', labelFr: 'Brouillon', colorHex: '#6B7280',       bgHex: C.muted.bg },
  emitted: { intent: 'info',    labelFr: 'Émise',     colorHex: C.info.color,    bgHex: C.info.bg },
  sent:    { intent: 'info',    labelFr: 'Émise',     colorHex: C.info.color,    bgHex: C.info.bg },
  due:     { intent: 'warning', labelFr: 'Échéance',  colorHex: C.warning.color, bgHex: C.warning.bg },
  paid:    { intent: 'success', labelFr: 'Payée',     colorHex: C.success.color, bgHex: C.success.bg },
  payable: { intent: 'warning', labelFr: 'À payer',   colorHex: C.warning.color, bgHex: C.warning.bg },
}

// ─────────────────────────────────────────────────────────────────────
// Ligne de facturation (billing line — interne)
// ─────────────────────────────────────────────────────────────────────

export const BILLING_LINE_STATUS: Record<string, StatusConfig> = {
  draft:           { intent: 'neutral', labelFr: 'Brouillon',         colorHex: C.neutral.color, bgHex: C.neutral.bg },
  pending_partner: { intent: 'warning', labelFr: 'Chez le partenaire', colorHex: C.warning.color, bgHex: C.warning.bg },
  ready:           { intent: 'success', labelFr: 'Pret',              colorHex: C.success.color, bgHex: C.success.bg },
  invoiced:        { intent: 'info',    labelFr: 'Facture',           colorHex: C.info.color,    bgHex: C.info.bg },
  credited:        { intent: 'error',   labelFr: 'Avoir',             colorHex: C.error.color,   bgHex: C.error.bg },
}

// ─────────────────────────────────────────────────────────────────────
// Payout — virement recycleur
// ─────────────────────────────────────────────────────────────────────


// ─────────────────────────────────────────────────────────────────────
// Resolution — arbitrage pricing contrat vs hors contrat
// ─────────────────────────────────────────────────────────────────────

/**
 * Remplace : pages/RegulationPage.tsx `RESOLUTION_STATUS_LABELS`
 */
export const RESOLUTION_STATUS: Record<string, StatusConfig> = {
  matched_in_subscription: { intent: 'success', labelFr: 'Contrat',     colorHex: C.success.color, bgHex: C.success.bg },
  manual_pricing_required: { intent: 'warning', labelFr: 'Prix manuel', colorHex: C.warning.color, bgHex: C.warning.bg },
  out_of_contract:         { intent: 'error',   labelFr: 'Hors contrat',colorHex: C.errorSoft.color, bgHex: C.error.bg },
  pending:                 { intent: 'neutral', labelFr: 'En attente',  colorHex: C.neutral.color, bgHex: C.neutral.bg },
  approved:                { intent: 'success', labelFr: 'Approuve',    colorHex: C.success.color, bgHex: C.success.bg },
  rejected:                { intent: 'error',   labelFr: 'Rejete',      colorHex: C.errorSoft.color, bgHex: C.error.bg },
}

// ─────────────────────────────────────────────────────────────────────
// Macro flux — DIB / AMV / DID
// ─────────────────────────────────────────────────────────────────────

/**
 * Categorie macro de dechet :
 *   - DIB = Dechet Industriel Banal (vert — valorisable)
 *   - AMV = Matiere a Valeur (bleu — ressource)
 *   - DID = Dechet Industriel Dangereux (rouge — hazard)
 *
 * Remplace :
 *   - lib/referentiels.ts `MACRO_COLORS`
 *   - pages/DictionnairesPage.tsx `MACRO_COLORS`
 *   - pages/SupplyGapsPage.tsx `MACRO_COLORS`
 *   - components/SupplyGapsPartner.tsx `MACRO_COLORS`
 *   - lib/generateFluxCardPdf.ts `MACRO_COLORS` (PDF — utiliser hex)
 */
export const MACRO_COLORS: Record<string, { colorHex: string; bgHex: string; labelFr: string; rgb: [number, number, number] }> = {
  DIB: { colorHex: '#22c55e', bgHex: '#DCFCE7', labelFr: 'DIB', rgb: [34, 197, 94] },
  AMV: { colorHex: '#3b82f6', bgHex: '#DBEAFE', labelFr: 'AMV', rgb: [59, 130, 246] },
  DID: { colorHex: '#ef4444', bgHex: '#FEE2E2', labelFr: 'DID', rgb: [239, 68, 68] },
}

/** Helper : ne renvoie que la couleur hex (migration legacy MACRO_COLORS simple). */
export function getMacroColor(macro: string | null | undefined, fallback = '#7A7A8A'): string {
  if (!macro) return fallback
  return MACRO_COLORS[macro]?.colorHex ?? fallback
}

// ─────────────────────────────────────────────────────────────────────
// Type waste — nomenclature matiere
// ─────────────────────────────────────────────────────────────────────

/**
 * Remplace : pages/PartnerPortal.shared.ts `WASTE_LABELS`.
 * Expose uniquement le label francais canonique.
 */
export const WASTE_LABELS: Record<string, string> = {
  DIB:       'DIB',
  bois:      'Bois',
  carton:    'Carton',
  ferraille: 'Ferraille',
  plastique: 'Plastique',
  dangereux: 'DID',
  gravats:   'Gravats',
  DEEE:      'DEEE',
  organique: 'Organique',
  autre:     'Autre',
}
